import { InfoIcon, WarningCircleIcon, XIcon } from '@phosphor-icons/react';
import Input from "@/core/components/dv-forms/input";
import Select from "@/core/components/dv-forms/select";
import { Controller, useForm } from 'react-hook-form';
import CheckboxField from '@/core/components/forms/fields/checkbox-input';
import ListCoins from '@/core/components/modal/dca/add-coins';
import { useEffect, useState, useRef } from 'react';
import DvModal from '@/core/components/modal/dv-modal';
import { useDcaStrategyStore } from '@/store/dca-strategy/store';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useModalStore } from '@/store/modal/store';
import { get } from 'lodash';
import { Tooltip } from 'react-tooltip';
import { listDcaFrequency } from '@/store/dca-strategy/constant';
import { DcaStatus } from '@/store/dca-strategy/enum';
import { DcaService } from '@/core/services/dcaService';
import { CoinModelRequest, DcaStrategy } from '@/types/dca';
import { useTranslation } from 'react-i18next';
import { useDcaValidation } from '@/core/utils/validate-investment';
import DcaStatusIndicator from '@/core/components/dca/DcaStatusIndicator';

const CreateDcaModal = ({ onClose, onSuccess }: { onClose: () => void, onSuccess: () => void }) => {
  const { t } = useTranslation();
  const { validateInvestmentAmount, validateMinimumInvestment } = useDcaValidation();
  const setModal = useModalStore((state) => state.setModal);
  const modalTransfer = useModalStore((state) => state.modal.transfer);
  const [openAddCoins, setOpenAddCoins] = useState(false);

  // Zustand 5 explicit selectors for reliable re-renders
  const dcaStrategyCreate = useDcaStrategyStore((state) => state.dcaStrategyCreate);
  const setToDcaWallet = useDcaStrategyStore((state) => state.setToDcaWallet);
  const setDcaStrategyCreate = useDcaStrategyStore((state) => state.setDcaStrategyCreate);
  const setDcaStrategyDetail = useDcaStrategyStore((state) => state.setDcaStrategyDetail);
  const setRefresh = useDcaStrategyStore((state) => state.setRefresh);

  // Balance refresh state
  const [isRefreshingBalance, setIsRefreshingBalance] = useState(false);
  const previousBalance = useRef(dcaStrategyCreate?.investedCapital ?? 0);
  const wasTransferModalOpen = useRef(false);
  const { register, handleSubmit, control, formState: { errors }, watch, setValue, getValues, reset, clearErrors, trigger } = useForm({
    defaultValues: {
      ...dcaStrategyCreate,
      name: dcaStrategyCreate.name,
      coinModelRequest: get(dcaStrategyCreate, 'coinModelRequest', []),
      investedCapital: get(dcaStrategyCreate, 'investedCapital', 0),
      frequency: get(dcaStrategyCreate, 'frequency', 'Daily'),
      performanceObjective: get(dcaStrategyCreate, 'performanceObjective', 0),
      transactionFee: 1,
      alertAndKeepPortfolio: get(dcaStrategyCreate, 'alertAndKeepPortfolio', false),
      alertAndSellPortfolio: get(dcaStrategyCreate, 'alertAndSellPortfolio', false),
      minimumInvestmentAmount: get(dcaStrategyCreate, 'minimumInvestmentAmount', 0),
      dcaStatus: DcaStatus.EDITING,
      dcaModelId: dcaStrategyCreate.dcaModelId
    } as DcaStrategy,
    mode: 'onChange',
  });
  const coinModelRequest = watch('coinModelRequest');

  const { mutate: updateDCAStrategy, isLoading: isUpdatingDca } = useMutation({
    mutationFn: (values: DcaStrategy) => DcaService.updateDCAStrategy({
      ...values,
      dcaStatus: DcaStatus.ACTIVE,
    }),
    onSuccess: (data) => {
      setRefresh(new Date().getTime());
      setDcaStrategyDetail(data);
      toast.success(t("message.dca_create_success"));
      onSuccess();
    },
  });

  const { mutate: handleAddCoins, isLoading: isCreatingDca } = useMutation({
    mutationFn: (data: CoinModelRequest[]) => DcaService.createDCAStrategy({
      ...getValues(),
      coinModelRequest: data,
    }),
    onSuccess: (data) => {
      const dcaModel = {
        ...data,
        // API returns basis points (0..10000). Convert back to UI percent (0..100) with 2 decimals for display/editing
        coinModelRequest: data.dcaStrategyAllocations.map((wallet) => ({
          name: wallet.coinCode,
          percentage: Math.round(((wallet.percentage ?? 0) / 100) * 100) / 100,
        })),
      };
      setOpenAddCoins(false);
      setDcaStrategyCreate(dcaModel);
      reset(dcaModel);
    },
  });

  const handleCreateDca = (values: DcaStrategy) => {
    // TODO: Create the DCA strategy
    updateDCAStrategy(values);
  }

  const handleAddDcaBalance = () => {
    setToDcaWallet(dcaStrategyCreate);
    setModal({ transfer: true });
  }

  // Detect transfer modal closing → show waiting message
  useEffect(() => {
    if (wasTransferModalOpen.current && !modalTransfer) {
      setIsRefreshingBalance(true);

      // Safety timeout: hide after 5 seconds if no update
      const timeoutId = setTimeout(() => {
        setIsRefreshingBalance(false);
      }, 5000);

      wasTransferModalOpen.current = false;
      return () => clearTimeout(timeoutId);
    }
    wasTransferModalOpen.current = modalTransfer ?? false;
  }, [modalTransfer]);

  // Detect store balance changes and update ONLY investedCapital (preserve other form data)
  useEffect(() => {
    const newBalance = dcaStrategyCreate?.investedCapital ?? 0;

    if (newBalance !== previousBalance.current) {
      setValue("investedCapital", newBalance);
      trigger("investedCapital");

      // Disable waiting message when balance updates
      if (isRefreshingBalance && newBalance > previousBalance.current) {
        setIsRefreshingBalance(false);
      }

      previousBalance.current = newBalance;
    }
  }, [dcaStrategyCreate?.investedCapital, isRefreshingBalance, setValue, trigger]);

  return (
    <div className="general-modal dca-form-modal">
      <div className="general-modal__header">
        {t('dca.dca_strategy')}
        <div className="general-modal__close" onClick={onClose}>
          <XIcon />
        </div>
      </div>
      <div className="general-modal__content">
        <DcaStatusIndicator dcaStrategy={getValues()} variant="compact" />
        <div className="dca__form">
          <Input
            disabled={true}
            type="text"
            label={
              <div className="d-flex gap-2 align-items-center">
                <span>{t('common.coins')}</span>
                <div data-tooltip-id={`dca-tooltip-create`} className="d-flex cursor-pointer">
                  <InfoIcon size={18} weight="fill" color="#CFCFCF" />
                </div>
                {
                  coinModelRequest?.some((coin: CoinModelRequest) => coin.percentage > 0) && (
                    <Tooltip
                      id={`dca-tooltip-create`}
                      render={() => (
                        <div className="d-flex gap-2">
                          <div>
                            {coinModelRequest.map((coin: CoinModelRequest) => (
                              <div key={coin.name}>
                                {coin.name}:
                              </div>
                            ))}
                          </div>
                          <div>
                            {coinModelRequest.map((coin: CoinModelRequest) => (
                              <div key={coin.name}>
                                {typeof coin.percentage === 'number' ? coin.percentage.toFixed(2) : coin.percentage}%
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      place="bottom"
                    />
                  )
                }
              </div>
            }
            name="coins"
            value={coinModelRequest?.map((coin) => coin.name).join(', ')}
            register={register}
            rules={{ validate: () => (coinModelRequest ?? []).length > 0 || t("form.required.add_coin") }}
            errors={errors}
            suffix={
              <button disabled={dcaStrategyCreate?.isAddCoins} onClick={() => setOpenAddCoins(true)} className="cta-button cta-small cta-blue">
                <span>{t('common.add')}</span>
              </button>
            }
          />
          <Input
            disabled={true}
            startAdornment="USDC"
            type="number"
            label={
              <div className="d-flex gap-2 align-items-center">
                <span>{t('dca.dca_balance')}</span>
                {isRefreshingBalance && (
                  <span className="text-warning animate-pulse" style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span className="spinner-small" /> {t('common.updating_balance')}
                  </span>
                )}
              </div>
            }
            name="investedCapital"
            register={register}
            rules={{
              required: t("form.required.dca_balance"),
              validate: (value) => validateInvestmentAmount(value, getValues())
            }}
            errors={errors}
            suffix={
              <button disabled={(coinModelRequest ?? []).length === 0} onClick={handleAddDcaBalance} className="cta-button cta-small cta-blue">
                <span>{t('common.add')}</span>
              </button>
            }
          />
          <Input
            type="number"
            label={t('dca.investment_amount_per_frequency')}
            name="minimumInvestmentAmount"
            register={register}
            rules={{
              required: t("form.required.investment_amount_per_frequency"),
              valueAsNumber: true,
              validate: (value) => validateMinimumInvestment(value, getValues(), true)
            }}
            errors={errors}
          />
          <Controller
            name="frequency"
            control={control}
            rules={{ validate: (value) => !!value || 'Frequency is required' }}
            render={({ field }) => (
              <Select
                label={t('common.frequency')}
                placeholder=""
                options={listDcaFrequency.map((l) => ({
                  content: t(l.content),
                  value: l.value
                }))}
                {...field}
                errors={errors}
              />
            )}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <Input
              suffix="%"
              type="number"
              label={t('dca.performance_objective')}
              name="performanceObjective"
              register={register}
              rules={{
                required: t("form.requried.performance_objective"),
                valueAsNumber: true,
                min: { value: 1, message: t("form.invalid.min_performance_objective") },
                max: { value: 100, message: t("form.invalid.max_performance_objective") },
              }}
              errors={errors}
            />
            <Input
              suffix="%"
              type="number"
              label={t('common.transaction_fee')}
              name="transactionFee"
              register={register}
              rules={{ required: t("form.required.transaction_fee") }}
              errors={errors}
              disabled={true}
            />
          </div>
          <Controller
            name="alertAndKeepPortfolio"
            control={control}
            rules={{ validate: () => (getValues('alertAndSellPortfolio') || getValues('alertAndKeepPortfolio')) || t("form.required.must_check_at_least") }}
            render={({ field }) => (
              <CheckboxField
                checked={field.value}
                onChange={(value) => {
                  field.onChange(value)
                  if (value) {
                    setValue('alertAndSellPortfolio', false);
                    clearErrors('alertAndKeepPortfolio');
                    clearErrors('alertAndSellPortfolio');
                  }
                }}
                label={t('dca.alert_by_email_and_keep_portfolio')}
              />
            )}
          />
          <Controller
            name="alertAndSellPortfolio"
            control={control}
            rules={{ validate: () => (getValues('alertAndSellPortfolio') || getValues('alertAndKeepPortfolio')) || t("form.required.must_check_at_least") }}
            render={({ field }) => (
              <CheckboxField
                checked={field.value}
                onChange={(value) => {
                  field.onChange(value)
                  if (value) {
                    setValue('alertAndKeepPortfolio', false);
                    clearErrors('alertAndSellPortfolio');
                    clearErrors('alertAndKeepPortfolio');
                  }
                }}
                label={t('dca.alert_by_email_and_when_objective_is_reached_sell_portfolio')}
              />
            )}
          />
          {
            (errors.alertAndKeepPortfolio || errors.alertAndSellPortfolio) && (
              <div className="danger-text">
                <WarningCircleIcon size={16} /> {t("form.required.must_check_at_least")}
              </div>
            )
          }
          <div className="paragraph-sm warning-text">
            {t('dca.note')}
          </div>
          <div className="paragraph-sm" style={{ margin: '5px 0 10px 0' }}>
            {t('dca.information')}
          </div>
        </div>
      </div>
      <div className="general-modal__footer general-modal__footer--right">
        <button className="cta-button cta-medium cta-white" onClick={onClose}>
          <span>{t('common.close')}</span>
        </button>
        <button disabled={isUpdatingDca} className="cta-button cta-medium cta-blue" onClick={handleSubmit(handleCreateDca)}>
          <span>{t('dca.create_dca')}</span>
        </button>
      </div>
      <DvModal open={openAddCoins} onClose={() => setOpenAddCoins(false)}>
        <ListCoins onClose={() => setOpenAddCoins(false)} dcaModel={getValues()} isCreatingDca={isCreatingDca} handleAddCoins={handleAddCoins} />
      </DvModal>
    </div>
  )
}

export default CreateDcaModal
